import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Camera, ChatsCircle, ListChecks, Receipt, Gear, ArrowRight,
  HandWaving, CheckCircle, Question, Bank, ShieldCheck, UserCircle,
} from "@phosphor-icons/react";

/**
 * Welcome / Quick start — a 60-second role-aware tour for a user logging in
 * for the first time. Auto-redirected here once (tracked via localStorage)
 * and also reachable any time from Settings or the help link.
 *
 * Designed with the dyslexia accessibility constraint in mind: large icons,
 * short sentences, no dense tables or fractions, color-coded by role.
 */
export default function Welcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "staff";

  const dismiss = () => {
    if (user?.id) localStorage.setItem(`welcomeSeen_${user.id}`, "1");
    navigate("/");
  };

  const palette = {
    owner: { color: "#002FA7", label: "Owner", icon: ShieldCheck },
    bookkeeper: { color: "#2CA01C", label: "Bookkeeper", icon: Bank },
    staff: { color: "#7C3AED", label: "Business administrator", icon: UserCircle },
  }[role] || { color: "#002FA7", label: "Member", icon: UserCircle };
  const RoleIcon = palette.icon;

  return (
    <div data-testid="welcome-page" className="p-6 sm:p-8 max-w-[900px] mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 text-[12px] uppercase tracking-[0.15em] text-[#525252] font-semibold">
          <HandWaving size={14} weight="duotone" />
          Welcome
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
          Hi {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-[#525252] mt-4 max-w-xl text-base leading-relaxed">
          This portal replaces email for Todd&apos;s Nursery and North Star monthly bookkeeping work.
          Everything lives here so nothing gets lost in inboxes.
        </p>
        <div
          className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold"
          style={{ backgroundColor: `${palette.color}15`, color: palette.color }}
          data-testid="welcome-role-badge"
        >
          <RoleIcon size={16} weight="duotone" />
          You&apos;re signed in as <span className="font-bold">{palette.label}</span>
        </div>
      </div>

      {role === "owner" && <OwnerTour color={palette.color} />}
      {role === "bookkeeper" && <BookkeeperTour color={palette.color} />}
      {role === "staff" && <StaffTour color={palette.color} />}

      <Section title="The 5 tabs at the bottom (mobile) or left (desktop)">
        <TabRow icon={ListChecks} name="Overview" desc="Today's dashboard. What needs your attention right now." />
        <TabRow icon={ListChecks} name="Monthly" desc="The recurring checklist — bank statements, loan docs, tax forms." />
        <TabRow icon={ChatsCircle} name="Inquiries" desc="Q&A board between you and the bookkeeper. Replaces back-and-forth emails." />
        <TabRow icon={Receipt} name="Remit" desc="Remittance advices (checks/ACHs). Snap or upload, the app extracts everything." />
        <TabRow icon={Receipt} name="Spend" desc="Vendor bills + receipts. Snap or upload, the app extracts everything." />
      </Section>

      <Section title="If you forget anything…">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/help"
            data-testid="welcome-deep-dive-link"
            className="border border-[#E5E5E5] hover:border-[#002FA7] hover:bg-[#002FA7]/5 p-4 transition-all flex items-start gap-3"
          >
            <Question size={20} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-semibold">Full Help guide</div>
              <div className="text-xs text-[#525252] mt-1">A page-by-page walkthrough with every button explained.</div>
            </div>
          </Link>
          <Link
            to="/settings"
            data-testid="welcome-settings-link"
            className="border border-[#E5E5E5] hover:border-[#002FA7] hover:bg-[#002FA7]/5 p-4 transition-all flex items-start gap-3"
          >
            <Gear size={20} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-semibold">Settings</div>
              <div className="text-xs text-[#525252] mt-1">Change your password, notification preferences, and more.</div>
            </div>
          </Link>
        </div>
      </Section>

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        <button
          onClick={dismiss}
          className="btn-ikb px-6 py-3 flex items-center justify-center gap-2 font-semibold"
          data-testid="welcome-got-it-btn"
        >
          <CheckCircle size={18} weight="fill" /> Got it — take me to my dashboard
        </button>
        <Link
          to="/help"
          className="px-6 py-3 border border-[#E5E5E5] hover:border-[#002FA7] hover:text-[#002FA7] transition-all text-sm flex items-center justify-center gap-2"
          data-testid="welcome-deep-dive-secondary"
        >
          Read the full guide first <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`welcome-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <h2 className="font-heading text-lg font-semibold mb-3 text-[#171717]">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function TabRow({ icon: Icon, name, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 border border-[#E5E5E5] bg-white">
      <Icon size={18} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
      <div>
        <div className="font-heading font-semibold text-sm">{name}</div>
        <div className="text-xs text-[#525252] mt-0.5 leading-snug">{desc}</div>
      </div>
    </div>
  );
}

function Step({ n, title, children, color }) {
  return (
    <div className="flex gap-3 items-start" data-testid={`welcome-step-${n}`}>
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-white"
        style={{ backgroundColor: color }}
      >
        {n}
      </div>
      <div className="flex-1 pt-1.5">
        <div className="font-heading font-semibold text-base">{title}</div>
        <div className="text-sm text-[#525252] mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function OwnerTour({ color }) {
  return (
    <Section title="What you do as the Owner">
      <div className="space-y-5">
        <Step n={1} title="Set up QuickBooks (one time)" color={color}>
          Go to <strong>Settings → QuickBooks</strong> and tap Connect. Once linked, remittances auto-post into QBO.
        </Step>
        <Step n={2} title="Invite your team" color={color}>
          <strong>Settings → Team → Invite user</strong>. Add your bookkeeper and admin. Each gets their own login.
          Forgot their password? Tap the 🔑 key icon next to their name to set a new one.
        </Step>
        <Step n={3} title="Approve tax payments" color={color}>
          When your bookkeeper requests approval (bell icon lights up), open the row on the Monthly tab and tap Pay Now or Do Not Pay.
        </Step>
        <Step n={4} title="Watch the dashboard" color={color}>
          Overview shows the current month at a glance. Anything urgent appears at the top.
        </Step>
      </div>
    </Section>
  );
}

function BookkeeperTour({ color }) {
  return (
    <Section title="What you do as the Bookkeeper">
      <div className="space-y-5">
        <Step n={1} title="Open the Monthly tab" color={color}>
          See every bank statement, loan doc, and tax form for the active month. Green = submitted, gray = still needed.
        </Step>
        <Step n={2} title="Apply remittances to QBO" color={color}>
          Open the <strong>Remit</strong> tab. Each row shows the customer + invoice the app already matched. Tap Apply to post into QuickBooks.
        </Step>
        <Step n={3} title="Ask the owner questions" color={color}>
          Use the <strong>Inquiries</strong> tab to flag specific charges or checks. Replaces back-and-forth email threads.
        </Step>
        <Step n={4} title="Close the month" color={color}>
          End of month? Tap the close-month action on Monthly. The app checks that everything is uploaded and applied.
        </Step>
      </div>
    </Section>
  );
}

function StaffTour({ color }) {
  return (
    <Section title="What you do as the Business Administrator">
      <div className="space-y-5">
        <Step n={1} title="Snap a remittance" color={color}>
          When a check or ACH advice arrives, tap <strong>Snap advice</strong> on Overview (or the camera button on Remit). Your phone&apos;s camera opens.
          Take the photo. The app reads the payer, amount, and invoice for you.
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#525252] px-2 py-1 bg-[#F5F5F5]">
            <Camera size={12} weight="bold" /> Take as many photos as you need — uploads happen in the background
          </div>
        </Step>
        <Step n={2} title="Upload a monthly statement" color={color}>
          Got a PDF statement from the bank or a vendor? Open <strong>Monthly</strong>, find the row, tap Upload.
          The app figures out which month it belongs to.
        </Step>
        <Step n={3} title="Answer the bookkeeper" color={color}>
          When Poly asks about a charge or check, you&apos;ll see it on <strong>Inquiries</strong>. Tap the row, type your answer.
        </Step>
        <Step n={4} title="Snap receipts" color={color}>
          Vendor receipts go on the <strong>Spend</strong> tab. Same as remittances — tap, snap, done.
        </Step>
      </div>
    </Section>
  );
}
